import React, { useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Users,
  Activity,
  FileWarning,
  Hash,
  Clock,
  Download
} from "lucide-react";

export default function RejectedOrdersView({
  rejectedOrders = [],
  clientData,
  loading,
  rejectedOrdersRefetch,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const toISTString = (dt) => {
    if (!dt) return "--";

    const date = new Date(dt);

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: false,
    });
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getInitial = (name) => (!name ? "?" : name.trim().charAt(0).toUpperCase());

  const clientLookup = useMemo(() => {
    const map = {};
    (clientData || []).forEach((c) => {
      map[c.clientId] = c.name;
    });
    return map;
  }, [clientData]);

  const normalizeSide = (side) =>
    typeof side === "number" ? (side === 1 ? "BUY" : "SELL") : side || "N/A";

  const parseTime = (dt) => (dt ? new Date(dt) : null);

  // Sort batches by latest rejected time DESC
  const sortedRejected = useMemo(() => {
    return [...rejectedOrders].sort((a, b) => {
      const aTimes = (a.orders || [])
        .map((o) => parseTime(o.raw?.orderdatetime || o.orderdatetime))
        .filter(Boolean);

      const bTimes = (b.orders || [])
        .map((o) => parseTime(o.raw?.orderdatetime || o.orderdatetime))
        .filter(Boolean);

      const aMax = aTimes.length ? Math.max(...aTimes.map(Number)) : 0;
      const bMax = bTimes.length ? Math.max(...bTimes.map(Number)) : 0;

      return bMax - aMax;
    });
  }, [rejectedOrders]);

  // Apply search
  const processed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return sortedRejected;

    return sortedRejected.filter((batch) => {
      const batchSide = normalizeSide(batch.side);

      const topMatch =
        batch.symbol?.toLowerCase().includes(q) ||
        (batch.strategy || "").toLowerCase().includes(q) ||
        batchSide.toLowerCase().includes(q);

      const nestedMatch = batch.orders?.some((o) => {
        const oSide = normalizeSide(o.side);
        const clientName = clientLookup[o.clientId]?.toLowerCase() || "";

        return (
          (o.clientId || "").toLowerCase().includes(q) ||
          clientName.includes(q) ||
          (o.raw?.message || "").toLowerCase().includes(q) ||
          oSide.toLowerCase().includes(q)
        );
      });


      return topMatch || nestedMatch;
    });
  }, [sortedRejected, searchQuery]);

  const downloadCSV = () => {
    if (!processed.length) return;

    // Flatten all orders
    const allOrders = processed.flatMap(batch => {
      const batchSide = normalizeSide(batch.side);
      return (batch.orders || []).map(order => {
        const orderTime = order.raw?.orderdatetime || order.orderdatetime;
        return {
          Symbol: batch.cleanSymbol,
          Side: batchSide,
          Qty: order.qty,
          Strategy: batch.strategy,
          ClientName: clientLookup[order.clientId] || "Unknown",
          ClientID: order.clientId,
          OrderID: order.orderId || order.order_id || "N/A",
          ErrorMessage: (order.raw?.message || "No error message").replace(/,/g, " "), // sanitize commas
          Time: orderTime ? new Date(orderTime).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "--"
        };
      });
    });

    const headers = ["Symbol", "Side", "Qty", "Strategy", "ClientName", "ClientID", "OrderID", "ErrorMessage", "Time"];
    const csvContent = [
      headers.join(","),
      ...allOrders.map(row => headers.map(fieldName => row[fieldName]).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `rejected_orders_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-8 rounded-xl border border-gray-800 flex flex-col justify-center items-center h-full shadow-2xl">
        <RefreshCw className="w-10 h-10 text-purple-500 animate-spin mb-4" />
        <span className="text-gray-400 animate-pulse font-medium">
          Syncing rejected orders...
        </span>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1d29] rounded-xl border border-gray-800 shadow-2xl overflow-hidden flex flex-col h-full w-full font-sans">
      {/* HEADER */}
      <div className="shrink-0 p-4 md:p-6 border-b border-gray-800 bg-gradient-to-r from-[#1a1d29] to-[#151720]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
              <div className="bg-red-500/10 p-2 rounded-lg border border-red-500/20">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              Rejected Orders
              <span className="ml-2 bg-gray-800 text-gray-300 text-xs px-2.5 py-1 rounded-full border border-gray-700 font-mono">
                {processed.length}
              </span>
            </h2>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={downloadCSV}
              disabled={!processed.length}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1f2937] hover:bg-[#374151] text-gray-300 rounded-lg border border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
            >
              <Download size={16} />
              <span className="hidden md:inline">Download CSV</span>
            </button>
            <div className="relative group flex-1 md:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="bg-[#0f1116] text-gray-200 pl-10 pr-4 py-2.5 rounded-lg border border-gray-700 focus:border-purple-500 focus:ring-1 w-full md:w-64 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {/* Custom Scrollbar Styles */}
      <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>
      <div className="flex-1 relative min-h-0 z-[1]">
        <div className="absolute inset-0 overflow-auto sleek-scrollbar">
          <table className="w-full text-sm min-w-[1150px]">
            <thead className="bg-[#14161f] text-gray-400 text-xs uppercase sticky top-0 z-20 border-b border-gray-800">
              <tr>
                <th className="py-4 px-6 text-left">#</th>
                <th className="py-4 px-6 text-left">Symbol</th>
                <th className="py-4 px-6 text-center">Side</th>
                <th className="py-4 px-6 text-right">Total Qty</th>
                <th className="py-4 px-6 text-left">Strategy</th>
                <th className="py-4 px-6 text-center">Affected Clients</th>
                <th className="py-4 px-6 text-right">Last Rejected</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-800/50">
              {processed.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-16">
                    <Activity className="w-16 h-16 mb-4 opacity-20 mx-auto" />
                    <p className="text-gray-400 text-lg">No rejected orders found</p>
                  </td>
                </tr>
              ) : (
                processed.map((batch, idx) => {
                  console.log("batch--->", batch);
                  const id = idx;
                  const isExpanded = expandedRows[id];
                  const ordersList = batch.orders || [];
                  const batchSide = normalizeSide(batch.side);

                  const latestTime = (() => {
                    const times = ordersList
                      .map((o) => o.raw?.orderdatetime || o.orderdatetime)
                      .filter(Boolean);
                    if (!times.length) return null;
                    return new Date(Math.max(...times.map((t) => new Date(t).getTime())));
                  })();

                  return (
                    <React.Fragment key={id}>
                      <tr
                        onClick={() => toggleRow(id)}
                        className={`cursor-pointer ${isExpanded ? "bg-[#1c1f2b]" : "hover:bg-[#1f222e]"
                          }`}
                      >
                        <td className="py-4 px-6 text-right">
                          {isExpanded ? (
                            <ChevronUp size={16} className="text-purple-400" />
                          ) : (
                            <ChevronDown size={16} className="text-gray-500" />
                          )}
                        </td>

                        <td className="py-4 px-6">
                          <span className="text-white font-bold block text-base">
                            {batch.cleanSymbol}
                          </span>
                        </td>

                        <td className="py-4 px-6 text-center">
                          <span
                            className={`px-2 py-1 text-xs rounded font-bold ${batchSide === "BUY"
                              ? "bg-green-500/10 text-green-400"
                              : "bg-red-500/10 text-red-400"
                              }`}
                          >
                            {batchSide}
                          </span>
                        </td>

                        <td className="py-4 px-6 text-right text-gray-300 font-mono">{batch.qty}</td>

                        <td className="py-4 px-6">
                          <span className="inline-flex items-center gap-1 text-xs bg-gray-800 px-2 py-1 rounded border border-gray-700 text-purple-300">
                            <Hash size={12} /> {batch.strategy}
                          </span>
                        </td>

                        <td className="py-4 px-6 text-center">
                          <div className="inline-flex items-center gap-2 justify-center">
                            <Users size={16} />
                            {ordersList.length} Clients
                          </div>
                        </td>


                        <td className="py-4 px-6 text-right text-gray-400 font-mono">
                          {latestTime ? toISTString(latestTime) : "--"}
                        </td>
                      </tr>

                      {/* EXPANDED ROW */}
                      {isExpanded && (
                        <tr className="bg-[#111318]">
                          <td colSpan="7" className="p-0">
                            <div className="p-6">
                              <div className="flex items-center gap-2 mb-4">
                                <FileWarning className="w-4 h-4 text-red-400" />
                                <h4 className="text-xs uppercase font-bold text-gray-400 tracking-widest">
                                  Rejection Details per Client
                                </h4>
                              </div>

                              <div className="grid grid-cols-1 gap-4">
                                {[...ordersList]
                                  .sort((a, b) => {
                                    const t1 = new Date(a.raw?.orderdatetime || a.orderdatetime).getTime();
                                    const t2 = new Date(b.raw?.orderdatetime || b.orderdatetime).getTime();
                                    return t2 - t1;
                                  })
                                  .map((order, cIdx) => {
                                    const orderTime =
                                      order.raw?.orderdatetime || order.orderdatetime;
                                    const parsedOrderTime = orderTime
                                      ? new Date(orderTime)
                                      : null;

                                    return (
                                      <div
                                        key={order.orderId || cIdx}
                                        className="bg-[#161821] border border-gray-800 rounded-lg p-4 flex flex-col md:flex-row gap-4"
                                      >
                                        <div className="flex items-center gap-4 border-r pr-4">
                                          <div className="bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center">
                                            <span className="text-sm font-bold text-gray-300">
                                              {getInitial(clientLookup[order.clientId])}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-white font-bold text-sm block">
                                              {clientLookup[order.clientId] || "Unknown"}
                                            </span>
                                            <span className="text-xs text-gray-500 block">
                                              Client ID: {order.clientId}
                                            </span>
                                            <span className="text-xs text-gray-500 block">
                                              Order ID: {order.orderId || order.order_id || "N/A"}
                                            </span>
                                            <span className="text-xs text-gray-500 block">
                                              Qty: {order.qty}
                                            </span>

                                            {/* NEW: Rejected at time */}
                                            <span className="text-xs text-gray-400 block mt-1 flex items-center gap-1">
                                              <Clock size={10} className="opacity-70" />
                                              {parsedOrderTime
                                                ? parsedOrderTime.toLocaleString("en-IN", {
                                                  hour12: false,
                                                })
                                                : "--"}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="flex-1">
                                          <div className="text-[10px] text-red-400 font-bold uppercase mb-1 flex items-center gap-1">
                                            <AlertCircle size={10} /> Broker Reason
                                          </div>
                                          <p className="text-gray-300 text-xs font-mono bg-[#0f1116] p-3 rounded border border-red-900/20">
                                            {order.raw?.message ||
                                              "No error message provided."}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div >
  );
}
